import React from 'react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import Incident16Icon from '@atlaskit/icon-object/glyph/incident/16';
import MobileIcon from '@atlaskit/icon/glyph/mobile';
import NotificationDirectIcon from '@atlaskit/icon/glyph/notification-direct';
import PeopleIcon from '@atlaskit/icon/glyph/people-group';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import { Flex, Text, Stack } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import stepOneVectorDark from '../../../../../common/assets/step_one_vector_dark.png';
import stepOneVectorLight from '../../../../../common/assets/step_one_vector_light.png';
import { IconBoxColor, IconBox } from '../../../../../common/ui/icon-box';
import { WizardInfoItem } from '../../../../../common/ui/wizard/info-item';
import { Layout } from '../../../../../common/ui/wizard/layout';
import { messages as commonMessages } from '../../../../../common/ui/wizard/layout/messages';
import { messages } from './messages';
import type { StepOneProps } from './types';

export const StepOne = ({ onAction, onClose }: StepOneProps) => {
	const { formatMessage } = useIntl();

	const primaryActionButton = (
		<Button onClick={() => onAction((prevState) => prevState + 1)} appearance="primary">
			{formatMessage(commonMessages.nextButtonNonFinal)}
		</Button>
	);

	const secondaryActionButton = (
		<Button onClick={onClose} appearance="default">
			{formatMessage(commonMessages.closeButtonNonFinal)}
		</Button>
	);

	return (
		<Layout
			vector={{ light: stepOneVectorLight, dark: stepOneVectorDark }}
			currentStep={1}
			actionButtons={{
				primary: primaryActionButton,
				secondary: secondaryActionButton,
			}}
			onClose={onClose}
		>
			<Flex direction="column" rowGap="space.200">
				<Flex rowGap="space.100" direction="column">
					<Heading size="large">{formatMessage(messages.titleNonFinal)}</Heading>
					<Text>{formatMessage(messages.descriptionNonFinal)}</Text>
				</Flex>
				<Stack space="space.100">
					<WizardInfoItem
						iconBox={
							<IconBox
								// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
								icon={NotificationDirectIcon}
								color={IconBoxColor.Orange}
								withCustomContainer
							/>
						}
						title={formatMessage(messages.alertsTitleNonFinal)}
						description={formatMessage(messages.alertsDescriptionNonFinal)}
					/>
					<WizardInfoItem
						// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
						iconBox={<IconBox icon={SettingsIcon} color={IconBoxColor.Lime} withCustomContainer />}
						title={formatMessage(messages.operationsTitleNonFinal)}
						description={formatMessage(messages.operationsDescriptionNonFinal)}
					/>
					<WizardInfoItem
						// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
						iconBox={<IconBox icon={MobileIcon} color={IconBoxColor.Teal} withCustomContainer />}
						title={formatMessage(messages.mobileTitleNonFinal)}
						description={formatMessage(messages.mobileDescriptionNonFinal)}
					/>
					<WizardInfoItem
						// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
						iconBox={<IconBox icon={PeopleIcon} color={IconBoxColor.Blue} withCustomContainer />}
						title={formatMessage(messages.teamsTitleNonFinal)}
						description={formatMessage(messages.teamsDescriptionNonFinal)}
					/>
					<WizardInfoItem
						iconBox={
							// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
							<IconBox icon={Incident16Icon} color={IconBoxColor.Purple} withCustomContainer />
						}
						title={formatMessage(messages.incidentsTitleNonFinal)}
						description={formatMessage(messages.incidentsDescriptionNonFinal)}
					/>
				</Stack>
			</Flex>
		</Layout>
	);
};
